import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import Footer from "../main/Footer";
import { isAutheticated } from "../auth/authhelper";
import { API } from "../../API";

const WatchTimeGraphs = () => {
    const { token } = isAutheticated();
    const [channelData, setChannelData] = useState([]);
    const [videoData, setVideoData] = useState([]);
    const [channels, setChannels] = useState([]);
    const [subchannels, setSubchannels] = useState([]);
    const [selectedChannelId, setSelectedChannelId] = useState("");
    const [selectedSubchannelId, setSelectedSubchannelId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [subchannelGraphData, setSubchannelGraphData] = useState([]);
    const [subchannelLoading, setSubchannelLoading] = useState(false);
    const [subchannelName, setSubchannelName] = useState("");
    const [subchannelError, setSubchannelError] = useState("");
    const [loadingChannels, setLoadingChannels] = useState(false);
    const [loadingChannelStats, setLoadingChannelStats] = useState(false);

    // Axios instance
    const apiClient = axios.create({
        baseURL: API,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    // Fetch overall channel stats
    useEffect(() => {
        fetchChannelData();
    }, [fromDate, toDate]);

    // Fetch video data when channel selected
    useEffect(() => {
        if (selectedChannelId) {
            fetchChannelStats(selectedChannelId);
        }
    }, [selectedChannelId, fromDate, toDate]);

    // Fetch video data when subchannel selected
    useEffect(() => {
        if (selectedSubchannelId) {
            fetchSubchannelVideos(selectedSubchannelId);
        } else {
            setSubchannelGraphData([]);
            setSubchannelName("");
            setSubchannelError("");
        }
    }, [selectedSubchannelId, fromDate, toDate]);

    // 1. Fetch all channels list + overall stats
    const fetchChannelData = async () => {
        setLoadingChannels(true);
        try {
            const response = await apiClient.get(
                "/api/bandwidthWithwatchTime/admin/channel-stats",
                { params: { fromDate, toDate } }
            );

            const formattedData = response.data.channels.map(item => ({
                channelId: item.channelId,
                channelName: item.name,
                totalWatchTime: Number(item.totalWatchTime / 3600).toFixed(2),
                totalViews: item.totalViews,
            }));

            setChannelData(formattedData);

            setChannels(
                response.data.allChannelList.map(c => ({
                    channelId: c._id,
                    channelName: c.name,
                }))
            );
        } catch (error) {
            console.error("Error fetching channel watch time data:", error);
        } finally {
            setLoadingChannels(false);
        }
    };

    // 2. Fetch single channel stats (with subchannels + videos)
    const fetchChannelStats = async (channelId) => {
        setLoadingChannelStats(true);
        try {
            const response = await apiClient.get(`/api/bandwidthWithwatchTime/admin/channel-stats/${channelId}`, {
                params: { fromDate, toDate }
            });

            setSubchannels(response.data.subchannels || []);

            const formattedVideos = (response.data.videos || []).map(item => ({
                videoTitle: item.title,
                totalWatchTime: Number(item.totalWatchTime / 3600).toFixed(2),
                views: item.views,
                date: item.date,
                month: item.month
            }));

            setVideoData(formattedVideos);
            setSelectedSubchannelId("");
        } catch (error) {
            console.error("Error fetching channel stats:", error);
        } finally {
            setLoadingChannelStats(false);
        }
    };

    // 3. Fetch videos of selected subchannel
    const fetchSubchannelVideos = async (subchannelId) => {
        setSubchannelLoading(true);
        setSubchannelError("");
        setSubchannelName("");
        try {
            const response = await apiClient.get(
                `/api/bandwidthWithwatchTime/video_watch/watch_time/channel/${subchannelId}`,
                { params: { fromDate, toDate } }
            );

            // Handle new API response structure
            if (response.data && response.data.videos) {
                setSubchannelName(response.data.channelName || "");
                const formattedVideos = response.data.videos.map(item => ({
                    videoTitle: item.title,
                    totalWatchTime: Number(item.totalWatchTime / 3600).toFixed(2),
                    views: item.views
                }));
                setSubchannelGraphData(formattedVideos);
                if (formattedVideos.length === 0) {
                    setSubchannelError("No video data found for this channel.");
                }
            } else {
                setSubchannelGraphData([]);
                setSubchannelError("No video data found for this channel.");
            }
        } catch (error) {
            setSubchannelGraphData([]);
            setSubchannelError("Error loading subchannel data. Please try again.");
            console.error("Error fetching subchannel videos:", error);
        } finally {
            setSubchannelLoading(false);
        }
    };

    // Modern loading spinner
    const Loader = () => (
        <div className="flex items-center justify-center py-12">
            {/* <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                />
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                />
            </svg> */}
            <span className="ml-3 text-indigo-600 font-medium text-sm">Loading...</span>
        </div>
    );

    return (
        <div className="main-content bg-gray-50 min-h-screen">
            <div className="page-content">
                <div className="container-fluid">

                    {/* Filters Section */}
                    <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                        <h2 className="text-2xl font-semibold mb-4 text-center">Watch Time Analytics</h2>
                        {/* <div className="flex flex-row gap-4 justify-center">
                            <div className="flex-1 max-w-xs">
                                <label className="block mb-1 font-medium pl-2">From Date</label>
                                <input
                                    type="date"
                                    className="border p-2 rounded-md w-full"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="flex-1 max-w-xs">
                                <label className="block mb-1 font-medium pl-2">To Date</label>
                                <input
                                    type="date"
                                    className="border p-2 rounded-md w-full"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                        </div> */}
                    </div>

                    {/* Channel Watch Time Histogram */}
                    <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                        <h2 className="text-2xl font-semibold mb-4 pl-2">NetWork Channel Watch Time</h2>
                        {loadingChannels ? (
                            <Loader />
                        ) : channelData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={channelData}>
                                    <XAxis dataKey="channelName" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="totalWatchTime" fill="#4F46E5" name="Watch Time (hrs)" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="totalViews" fill="#10B981" name="Views" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : loadingChannels ? (
                            <Loader />
                        ) : (
                            <div className="text-center text-gray-500 py-12">
                                No channel data found for your search. Please adjust your filters or try again.
                            </div>
                        )}
                    </div>

                    {/* Channel Selection Dropdown */}
                    {channels.length > 0 && (
                        <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                            <h2 className="text-2xl font-semibold mb-4 pl-2">Select a NetWork Channel</h2>
                            <select
                                className="border p-2 rounded-md w-full"
                                value={selectedChannelId}
                                onChange={(e) => setSelectedChannelId(e.target.value)}
                            >
                                <option value="">Select a NetWork Channel</option>
                                {channels.map(({ channelId, channelName }) => (
                                    <option key={channelId} value={channelId}>
                                        {channelName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Video Watch Time Histogram */}
                    {selectedChannelId && (
                        <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                            <h2 className="text-2xl font-semibold mb-4 pl-2">
                                Video Watch Time for NetWork Channel
                            </h2>
                            {loadingChannelStats ? (
                                <Loader />
                            ) : videoData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart data={videoData}>
                                        <XAxis dataKey="videoTitle" tick={{ fontSize: 12 }} />
                                        <YAxis tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="totalWatchTime" fill="#6366F1" name="Watch Time (hrs)" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="views" fill="#F59E0B" name="Views" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : loadingChannelStats ? (
                                <Loader />
                            ) : (
                                <div className="text-center text-gray-500 py-12">
                                    No video data found for this channel.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Subchannel Selection Dropdown (only if channel selected and subchannels exist) */}
                    {selectedChannelId && subchannels.length > 0 && (
                        <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                            <h2 className="text-2xl font-semibold mb-4 pl-2">Select a Channel</h2>
                            <select
                                className="border p-2 rounded-md w-full"
                                value={selectedSubchannelId}
                                onChange={(e) => setSelectedSubchannelId(e.target.value)}
                            >
                                <option value="">Select a Channel</option>
                                {subchannels.map(({ _id, name }) => (
                                    <option key={_id} value={_id}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Subchannel Video Watch Time Histogram */}
                    {selectedSubchannelId && (
                        <div className="p-6 bg-white rounded-lg shadow-lg mb-6">
                            {subchannelLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {subchannelName && (
                                        <h3 className="text-xl font-bold mb-2 text-indigo-700 pl-2">
                                            Channel: {subchannelName}
                                        </h3>
                                    )}
                                    {subchannelGraphData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={400}>
                                            <BarChart data={subchannelGraphData}>
                                                <XAxis dataKey="videoTitle" tick={{ fontSize: 12 }} />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="totalWatchTime" fill="#10B981" name="Watch Time (hrs)" radius={[6, 6, 0, 0]} />
                                                <Bar dataKey="views" fill="#F59E0B" name="Views" radius={[6, 6, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : subchannelLoading ? (
                                        <Loader />
                                    ) : (
                                        <div className="text-center text-gray-500 py-12">
                                            {subchannelError || "No video data found for this channel."}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default WatchTimeGraphs;
